import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../api/dates";
import { WORK_STATUSES, type Card as CardKey, type TrackerRow, type WorkStatus } from "../../api/reports";
import { Card, Segmented } from "../../components/ui";

/** The PRV Tracker sheet as a table: every Red List works order, its status in
 * the client's words, its dates and what is holding it up. Narrowed by the
 * card or status pressed above, or by a search over the works order, the site
 * and the address. */

export type TrackerFocus = { kind: "card"; key: CardKey } | { kind: "status"; key: WorkStatus } | null;

const STATUS_CLASS: Record<WorkStatus, string> = {
  "Date Set": "rl-date-set",
  "To Be Confirmed": "rl-tbc",
  "On Hold - Issue": "rl-hold",
  "BOQ Pending": "rl-boq",
  "QCP Specs Done": "rl-qcp-done",
  "QCP Needed": "rl-qcp-needed",
  Completed: "rl-completed",
};

const SCHEDULE_CLASS: Record<string, string> = {
  Confirmed: "rl-confirmed",
  "Confirmed - Date Changed": "rl-changed",
  "Awaiting Confirmation": "rl-awaiting",
  "Not Scheduled": "rl-not-scheduled",
  Completed: "rl-completed",
};

export function statusClass(status: WorkStatus): string {
  return STATUS_CLASS[status];
}

function matches(row: TrackerRow, needle: string): boolean {
  if (needle === "") {
    return true;
  }
  return [row.work_order, row.site_name, row.address, row.func_loc, row.equip_no, row.contractor]
    .filter((part): part is string => part !== null)
    .some((part) => part.toLowerCase().includes(needle));
}

export function narrow(rows: readonly TrackerRow[], focus: TrackerFocus, search: string): TrackerRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (focus?.kind === "card" && row.card !== focus.key) {
      return false;
    }
    if (focus?.kind === "status" && row.work_status !== focus.key) {
      return false;
    }
    return matches(row, needle);
  });
}

export function RedListTracker({ rows, focus, onFocus }: { rows: readonly TrackerRow[]; focus: TrackerFocus; onFocus: (focus: TrackerFocus) => void }) {
  const [search, setSearch] = useState("");
  const shown = useMemo(() => narrow(rows, focus, search), [rows, focus, search]);
  const status = focus?.kind === "status" ? focus.key : "";
  return (
    <Card
      title="PRV Tracker"
      sub={`${shown.length} of ${rows.length} works orders`}
      className="rl-tracker"
      actions={
        <>
          <Segmented
            label="Work status"
            value={status}
            options={[{ value: "", label: "All" }, ...WORK_STATUSES.map((value) => ({ value, label: value }))]}
            onChange={(value) => onFocus(value === "" ? null : { kind: "status", key: value as WorkStatus })}
          />
          <input
            type="search"
            className="input"
            placeholder="Works order, site, address…"
            aria-label="Search the tracker"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </>
      }
      flush
    >
      <div className="table-card">
        <table className="data-table rl-table" aria-label="PRV Tracker">
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Work order</th>
              <th>Site</th>
              <th>Address</th>
              <th>Contractor</th>
              <th>Work status</th>
              <th>Proposed</th>
              <th>Actual</th>
              <th>Visited</th>
              <th>Schedule status</th>
              <th className="num">Change</th>
              <th>SAP finish</th>
              <th className="num">Overdue</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={14} className="muted">
                  No works orders match.
                </td>
              </tr>
            ) : (
              shown.map((row) => (
                <tr key={row.site_id}>
                  <td className="num">{row.number}</td>
                  <td className="mono">{row.work_order ?? "—"}</td>
                  <td>
                    <Link to={`/sites/${row.site_id}`}>{row.site_name}</Link>
                  </td>
                  <td className="muted">{row.address ?? "—"}</td>
                  <td>{row.contractor ?? <span className="muted">Not assigned</span>}</td>
                  <td>
                    <span className={`rl-chip ${STATUS_CLASS[row.work_status]}`}>{row.work_status}</span>
                  </td>
                  <td>{formatDate(row.proposed_date)}</td>
                  <td>{formatDate(row.confirmed_date)}</td>
                  <td>{row.site_visited ? "Yes" : "No"}</td>
                  <td>
                    <span className={`rl-chip ${SCHEDULE_CLASS[row.schedule_status] ?? ""}`}>{row.schedule_status}</span>
                  </td>
                  <td className="num">{row.date_change_days ?? ""}</td>
                  <td>{formatDate(row.finish_date)}</td>
                  <td className={row.days_overdue !== null && row.days_overdue >= 30 ? "num rl-overdue" : "num"}>{row.days_overdue ?? ""}</td>
                  <td className="rl-comments">
                    {row.comments ?? ""}
                    {row.plan_name ? <span className="muted"> · {row.plan_name}</span> : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
